import { css, Theme } from '@emotion/react'
import { useTheme } from '@emotion/react'
import { HealthInsurerDetail } from '@/types/HealthInsurerDetail'
import { DestinationDetail } from '@/types/destinationDetail'
import { compareDateYmd } from '@/utils/dateUtils'

const containerStyle = (t: Theme, width: number) => css`
  border: solid 1px;
  padding: 8px 16px;
  height: 55px;
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  width: ${width}px;
  max-width: 100%;
`
const colorVariantStyles = {
  primary: (t: Theme) => css`
    background: #d6d8d9;
    border-color: #c6c8ca;
  `,
  attention: (t: Theme) => css`
    border-color: #ff5100;
    color: #ff5100;
    background: #ff51001a;
  `,
}

export type ColorVariant = keyof typeof colorVariantStyles

export type DetailMessagePropTypes = {
  data: HealthInsurerDetail | DestinationDetail
  width?: number
}
export const DetailMessage = ({ data, width = 918 }: DetailMessagePropTypes) => {
  const dataTypeName =
    'insurerName' in data ? '保険者' : 'destinationName' in data ? '送達先' : ''
  return (
    <>
      {data.startDatePending && (
        <>
          {data.isObsoletePending !== '1' &&
            compareDateYmd(data.startDate, data.startDatePending) === 0 && (
              <MessageContainer color="attention" needIcon={true} width={width}>
                <span>
                  この{dataTypeName}情報は
                  {new Intl.DateTimeFormat('ja-JP', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }).format(new Date(data.startDatePending))}
                  に適用開始されます。
                </span>
              </MessageContainer>
            )}
          {data.isObsoletePending !== '1' &&
            compareDateYmd(data.startDate, data.startDateCurr ?? new Date()) ===
              0 &&
            compareDateYmd(data.startDate, data.startDatePending) < 0 && (
              <MessageContainer color="attention" needIcon={true} width={width}>
                <span>
                  {new Intl.DateTimeFormat('ja-JP', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }).format(new Date(data.startDatePending))}
                  以降に新しい{dataTypeName}情報が適用されます。
                </span>
                <span style={{ fontSize: '1.2rem', display: 'block' }}>
                  編集と廃止は適用待ちの{dataTypeName}情報のみ可能です。
                </span>
              </MessageContainer>
            )}
          {data.isObsoletePending === '1' &&
            compareDateYmd(data.startDate, data.startDateCurr ?? new Date()) >=
              0 && (
              <MessageContainer color="attention" needIcon={true} width={width}>
                <span>
                  この{dataTypeName}情報は
                  {new Intl.DateTimeFormat('ja-JP', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }).format(new Date(data.startDatePending))}
                  に廃止されます。
                </span>
              </MessageContainer>
            )}
        </>
      )}
      {data.startDateCurr && (
        <>
          {compareDateYmd(data.startDate, data.startDateCurr) < 0 && (
            <MessageContainer color="primary" needIcon={true} width={width}>
              <span>過去の{dataTypeName}情報を表示しています。</span>
            </MessageContainer>
          )}
          {compareDateYmd(data.startDate, data.startDateCurr) === 0 &&
            data.isObsolete === '1' && (
              <MessageContainer color="primary" needIcon={true} width={width}>
                <span>この{dataTypeName}情報は廃止されています。</span>
              </MessageContainer>
            )}
        </>
      )}
    </>
  )
}

type ContainerPropTypes = {
  children: React.ReactNode
  color: ColorVariant
  needIcon: boolean
  width: number
}

const MessageContainer = ({
  children,
  color,
  needIcon,
  width,
}: ContainerPropTypes) => {
  const theme = useTheme()
  return (
    <div css={[containerStyle(theme, width), colorVariantStyles[color](theme)]}>
      {needIcon && (
        <img src="/images/triangle-exclamation-icon.svg" alt="警告" />
      )}
      <div>{children}</div>
    </div>
  )
}
